import * as Constants from "../../core/Constants";
import { CoreInterfaces } from "../../core/Models";
import { useContext, useEffect, useState } from "react";
import { t } from "i18next";
import { isAfter, isValid, parseISO, startOfDay } from "date-fns";
import {
  fetchData,
  fetchEngagementLettersSummaryView,
  getSummaryBusinessAreaLabel,
  isAdministrator,
  isOfficeManager,
  parseDateOnly,
  toUtcMidnightISO,
} from "src/utils";
import GtEngagementLetterMenu from "../Common/GtEngagementLetterMenu";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EngagementLettersFilterPanel from "./EngagementLettersFilterPanel";
import { NavLink } from "react-router-dom";
import { LoadingContext } from "src/contexts/LoadingContext";
import { useSnackBar } from "src/contexts/SnackbarContext";
import GtChangeOwnerDialog from "../Common/GtChangeOwnerDialog";
import GtChangeTeamDialog from "../Common/GtChangeTeamDialog";
import GtInactivateEngagementDialog from "../Common/GtInactivateEngagementDialog";
import { AppContext } from "src/contexts/AppContext";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Tooltip,
} from "@mui/material";
import {
  DataGridPro,
  GridColumnVisibilityModel,
  GridPaginationModel,
  GridColDef,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { GridValueGetterParams } from "@mui/x-data-grid";
import { Team } from "src/core/Models/Core.interface";

const options: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "long",
  day: "numeric",
};

function formatInactivationDate(inactivationDate?: string | null): string {
  return parseDateOnly(inactivationDate)?.toLocaleDateString([], options);
}

function EngagementLettersSummaryViewPanel(): JSX.Element {
  const { globalState } = useContext(AppContext);
  const { showSnackBar } = useSnackBar();
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const { updateApplicationLoadingState } = useContext(LoadingContext);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [
    selectedEngagementLettersSummaryItem,
    setSelectedEngagementLettersSummaryItem,
  ] = useState(null);
  const [isGtChangeOwnerDialogVisible, setIsGtChangeOwnerDialogVisible] =
    useState(false);
  const [isGtChangeTeamDialogVisible, setIsGtChangeTeamDialogVisible] =
    useState(false);
  const [
    isGtInactivateEngagementDialogVisible,
    setIsGtInactivateEngagementDialogVisible,
  ] = useState(false);
  const [filter, setFilter] = useState(null);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "SigningDate", sort: "desc" },
  ]);

  const handleSortModelChange = (newModel: GridSortModel) => {
    setSortModel(newModel);
  };

  useEffect(() => {
    const activeSort = sortModel.length > 0 ? sortModel[0] : null;
    const isServerSortable =
      activeSort?.field === "LastModifiedDate" ||
      activeSort?.field === "SigningDate";

    if (activeSort && isServerSortable) {
      const direction =
        activeSort.sort === "asc"
          ? Constants.OrderDirection.Asc
          : Constants.OrderDirection.Desc;
      loadData(filter, paginationModel, direction, activeSort.field);
    } else {
      loadData(filter, paginationModel);
    }
  }, [filter, paginationModel, sortModel]);

  function loadData(
    filterData: any,
    paginationModel: GridPaginationModel,
    orderDirection?: Constants.OrderDirection,
    orderBy?: string
  ): void {
    updateApplicationLoadingState(true);
    fetchEngagementLettersSummaryView({
      Skip: paginationModel.page * paginationModel.pageSize,
      Take: paginationModel.pageSize,
      IsActive: true,
      ...filterData,
      OrderDirection: orderDirection,
      OrderBy: orderBy,
    })
      .then(
        (engagementLettersSummary: CoreInterfaces.EngagementLettersSummary) => {
          const items = [...engagementLettersSummary.Items];
          if (orderBy === "SigningDate" || orderBy === "LastModifiedDate") {
            items.sort((a, b) => {
              const dateA = a[orderBy] ? new Date(a[orderBy]).getTime() : 0;
              const dateB = b[orderBy] ? new Date(b[orderBy]).getTime() : 0;
              return orderDirection === Constants.OrderDirection.Asc
                ? dateA - dateB
                : dateB - dateA;
            });
          }
          setRows(items);
          setTotalCount(engagementLettersSummary.TotalCount);
          updateApplicationLoadingState(false);
        }
      )
      .catch(() => {
        updateApplicationLoadingState(false);
        showSnackBar(
          t("General.EngagementLettersSummaryViewLoadFail"),
          "error"
        );
      });
  }

  function setEngagementVersionActivationState(
    engagementLettersSummaryItem: CoreInterfaces.EngagementLettersSummaryItem,
    isActive: boolean,
    inactivationDate: string | null,
    successMessage?: string
  ): void {
    updateApplicationLoadingState(true);
    const payload: CoreInterfaces.EngagementConfigurationActivationUpdate = {
      EngagementConfigurationId: engagementLettersSummaryItem.Id,
      BusinessOpportunityId: engagementLettersSummaryItem.BusinessOpportunityId,
      IsActive: isActive,
      InactivationDate: inactivationDate,
    };
    fetchData(
      Constants.APIPath.SetEngagementVersionActivationState,
      Constants.APIMethod.PUT,
      payload
    )
      .then(() => {
        loadData(filter, paginationModel);
        if (successMessage) {
          showSnackBar(successMessage, "success");
        }
      })
      .catch(() => {
        updateApplicationLoadingState(false);
        showSnackBar(t("General.AnErrorHasOccured"), "error");
      });
  }

  function reactivateEngagement(
    engagementLettersSummaryItem: CoreInterfaces.EngagementLettersSummaryItem
  ): void {
    setEngagementVersionActivationState(
      engagementLettersSummaryItem,
      true,
      null
    );
  }

  function removeInactivationDate(
    engagementLettersSummaryItem: CoreInterfaces.EngagementLettersSummaryItem
  ): void {
    setEngagementVersionActivationState(
      engagementLettersSummaryItem,
      true,
      null,
      t("General.InactivationDateRemovedSuccess")
    );
  }

  function inactivateEngagement(
    engagementLettersSummaryItem: CoreInterfaces.EngagementLettersSummaryItem,
    inactivationDate: Date
  ): void {
    const isActive = isAfter(
      startOfDay(inactivationDate),
      startOfDay(new Date())
    );
    setIsGtInactivateEngagementDialogVisible(false);
    setEngagementVersionActivationState(
      engagementLettersSummaryItem,
      isActive,
      toUtcMidnightISO(inactivationDate),
      isActive
        ? t("General.InactivationDateScheduledSuccess", {
            date: inactivationDate.toLocaleDateString([], options),
          })
        : t("General.EngagementInactivatedSuccess")
    );
  }

  function inactivateEngagementDialogOpen(
    engagementLettersSummaryItem: CoreInterfaces.EngagementLettersSummaryItem
  ): void {
    setSelectedEngagementLettersSummaryItem(engagementLettersSummaryItem);
    setIsGtInactivateEngagementDialogVisible(true);
  }

  function changeOwnerDialogOpen(
    engagementLettersSummaryItem: CoreInterfaces.EngagementLettersSummary
  ): void {
    setSelectedEngagementLettersSummaryItem(engagementLettersSummaryItem);
    setIsGtChangeOwnerDialogVisible(true);
  }

  function changeTeamDialogOpen(
    engagementLettersSummaryItem: CoreInterfaces.EngagementLettersSummary
  ): void {
    setSelectedEngagementLettersSummaryItem(engagementLettersSummaryItem);
    setIsGtChangeTeamDialogVisible(true);
  }

  const columns = getColumnsDefinition(
    globalState,
    reactivateEngagement,
    inactivateEngagementDialogOpen,
    removeInactivationDate,
    changeOwnerDialogOpen,
    changeTeamDialogOpen
  );

  const toggleableColumns = columns.filter(
    (column) => column.field !== "actions" && !!column.headerName
  );

  return (
    <article>
      <EngagementLettersFilterPanel
        onFilter={(filterData) => {
          setFilter(filterData);
        }}
      ></EngagementLettersFilterPanel>
      <section className="gt-engagementLettersSummaryViewPanel__toolbar">
        <FormControl
          size="small"
          className="gt-engagementLettersSummaryViewPanel__columnPicker"
        >
          <InputLabel id="summary-columns-label">
            {t("General.Columns")}
          </InputLabel>
          <Select
            labelId="summary-columns-label"
            multiple
            value={toggleableColumns
              .filter((column) => columnVisibilityModel[column.field] !== false)
              .map((column) => column.field)}
            onChange={(event) => {
              const visibleFields = event.target.value as string[];
              const nextModel: GridColumnVisibilityModel = {};
              toggleableColumns.forEach((column) => {
                nextModel[column.field] = visibleFields.includes(column.field);
              });
              setColumnVisibilityModel(nextModel);
            }}
            input={<OutlinedInput label={t("General.Columns")} />}
            renderValue={(selected) =>
              `${(selected as string[]).length}/${toggleableColumns.length}`
            }
          >
            {toggleableColumns.map((column) => (
              <MenuItem key={column.field} value={column.field}>
                <Checkbox
                  checked={columnVisibilityModel[column.field] !== false}
                />
                <ListItemText primary={column.headerName} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </section>
      <section className="gt-engagementLettersSummaryViewPanel">
        <DataGridPro
          autoHeight
          getRowId={(row) => row.Id}
          rows={rows}
          columns={columns}
          rowHeight={44}
          columnHeaderHeight={52}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(model) =>
            setColumnVisibilityModel(model)
          }
          hideFooterPagination={false}
          hideFooterSelectedRowCount={true}
          pagination
          filterMode="server"
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={(nextPaginationModel) => {
            setPaginationModel(nextPaginationModel);
          }}
          pageSizeOptions={[10, 25, 50]}
          rowCount={totalCount}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          initialState={{
            sorting: {
              sortModel: [{ field: "SigningDate", sort: "desc" }],
            },
          }}
          getDetailPanelHeight={() => "auto"}
          getDetailPanelContent={({ row }) => (
            <article className="gt-summaryCell">
              <section className="gt-summaryCell__block">
                <div className="gt-summaryCell__block-row">
                  <span className="gt-summaryCell__block-row-item">
                    {t("General.ClientName")}:
                  </span>{" "}
                  {row.CompanyName}
                </div>
                <div className="gt-summaryCell__block-row">
                  {" "}
                  <span className="gt-summaryCell__block-row-item">
                    {t("General.GtOwner")}:
                  </span>{" "}
                  {row.OwnerFirstName + " " + row.OwnerLastName}
                </div>
                <div className="gt-summaryCell__block-row">
                  {" "}
                  <span className="gt-summaryCell__block-row-item">
                    {t("General.Team")}:
                  </span>{" "}
                  {row.Team?.DisplayName ? row.Team?.DisplayName : "---"}
                </div>
              </section>

              <section className="gt-summaryCell__block">
                <div className="gt-summaryCell__block-row">
                  <span className="gt-summaryCell__block-row-item">
                    {t("General.ClientOrgNumber")}:
                  </span>
                  {row.OrganisationNumber}
                </div>
                <div className="gt-summaryCell__block-row">
                  <span className="gt-summaryCell__block-row-item">
                    {t("General.OportunityId")}:
                  </span>
                  {row.BusinessOpportunityId}
                </div>
                <div className="gt-summaryCell__block-row">
                  <span className="gt-summaryCell__block-row-item">
                    {t("General.BusinessOpportunityDescription")}:
                  </span>
                  {row.BusinessOpportunityDescription}
                </div>
              </section>

              <section className="gt-summaryCell__block">
                <div className="gt-summaryCell__block-row">
                  {" "}
                  <span className="gt-summaryCell__block-row-item">
                    {t("General.CreatedDate")}:
                  </span>
                  {(row.CreatedDate &&
                    (() => {
                      const d = parseISO(row.CreatedDate);
                      return isValid(d)
                        ? d.toLocaleDateString([], {
                            ...options,
                            timeZone: "UTC",
                          })
                        : null;
                    })()) ||
                    "---"}
                </div>
                <div className="gt-summaryCell__block-row">
                  {" "}
                  <span className="gt-summaryCell__block-row-item">
                    {t("General.Status")}:
                  </span>{" "}
                  {t(
                    `Options.EngagementLetterSummaryStatus.${row.EngagementStatus}`
                  )}
                </div>
                <div className="gt-summaryCell__block-row">
                  {" "}
                  <span className="gt-summaryCell__block-row-item">
                    {t("General.SigningDate")}:
                  </span>{" "}
                  {(row.SigningDate &&
                    new Date(row.SigningDate).toLocaleDateString([], {
                      ...options,
                      timeZone: "UTC",
                    })) ||
                    "---"}
                </div>
                {row.IsActive && row.InactivationDate && (
                  <div className="gt-summaryCell__block-row">
                    {" "}
                    <span className="gt-summaryCell__block-row-item">
                      {t("General.PlannedInactivationDate")}:
                    </span>{" "}
                    {formatInactivationDate(row.InactivationDate) || "---"}
                  </div>
                )}
              </section>

              <section className="gt-summaryCell__block">
                <div className="gt-summaryCell__block-row">
                  <span className="gt-summaryCell__block-row-item">
                    {t("General.EngagementLetter")}:
                  </span>{" "}
                  {row.HasEngagementLetter ? (
                    <CheckIcon fontSize="small" color="success" />
                  ) : (
                    <CloseIcon fontSize="small" color="error" />
                  )}
                </div>
                <div className="gt-summaryCell__block-row">
                  <span className="gt-summaryCell__block-row-item">
                    {t("General.EngagementType")}:
                  </span>{" "}
                  {row.EngagementType
                    ? t(`Options.EngagementType.${row.EngagementType}`)
                    : "---"}
                </div>
              </section>
            </article>
          )}
        />
      </section>
      {isGtChangeOwnerDialogVisible && (
        <GtChangeOwnerDialog
          engagementLettersSummaryItem={selectedEngagementLettersSummaryItem}
          onOwnerChanged={function (): void {
            loadData(filter, paginationModel);
            setIsGtChangeOwnerDialogVisible(false);
          }}
          onChangeOwnerDialogClose={function (): void {
            setIsGtChangeOwnerDialogVisible(false);
          }}
        />
      )}
      {isGtChangeTeamDialogVisible && (
        <GtChangeTeamDialog
          engagementLettersSummaryItem={selectedEngagementLettersSummaryItem}
          onTeamChanged={function (): void {
            loadData(filter, paginationModel);
            setIsGtChangeTeamDialogVisible(false);
          }}
          onChangeTeamDialogClose={() => {
            setIsGtChangeTeamDialogVisible(false);
          }}
        />
      )}
      {isGtInactivateEngagementDialogVisible && (
        <GtInactivateEngagementDialog
          engagementLettersSummaryItem={selectedEngagementLettersSummaryItem}
          onConfirm={(inactivationDate: Date) => {
            inactivateEngagement(
              selectedEngagementLettersSummaryItem,
              inactivationDate
            );
          }}
          onDialogClose={() => {
            setIsGtInactivateEngagementDialogVisible(false);
          }}
        />
      )}
    </article>
  );
}

function getColumnsDefinition(
  appState: CoreInterfaces.AppState,
  onReactivateFn: Function,
  onInactivateDialogOpenFn: Function,
  onRemoveInactivationDateFn: Function,
  onChangeOwnerDialogOpenFn: Function,
  onChangeTeamDialogOpenFn: Function
): GridColDef[] {
  return [
    {
      field: "CompanyName",
      headerName: t("General.ClientName"),
      flex: 1.4,
      minWidth: 130,
      sortable: true,
      disableColumnMenu: true,
      cellClassName: (params: {
        row: CoreInterfaces.EngagementLettersSummaryItem;
      }) => {
        return `gt-lettersSummaryCell ${
          !params.row.IsActive ? "gt-lettersSummaryCell--deactivated" : ""
        }`;
      },
      renderCell(params: { row: CoreInterfaces.EngagementLettersSummaryItem }) {
        return (
          <article className="gt-whiteSpaceNoWrap">
            <Tooltip
              enterDelay={800}
              title={
                <article className="gt-descriptionTooltip">
                  <section className="gt-descriptionTooltip__title">
                    {t("General.ClientName")}
                  </section>
                  <section className="gt-descriptionTooltip__description">
                    {params.row.CompanyName}
                  </section>
                  <section className="gt-descriptionTooltip__title">
                    {t("General.ClientOrgNumber")}
                  </section>
                  <section className="gt-descriptionTooltip__description">
                    {params.row.OrganisationNumber}
                  </section>
                  <section className="gt-descriptionTooltip__title">
                    {t("General.OportunityId")}
                  </section>
                  <section className="gt-descriptionTooltip__description">
                    {params.row.BusinessOpportunityId}
                  </section>
                  <section className="gt-descriptionTooltip__title">
                    {t("General.BusinessOpportunityDescription")}
                  </section>
                  <section className="gt-descriptionTooltip__description">
                    {params.row.BusinessOpportunityDescription}
                  </section>
                </article>
              }
            >
              <span>{params.row.CompanyName}</span>
            </Tooltip>
          </article>
        );
      },
    },
    {
      field: "SigningDate",
      headerName: t("General.SigningDate"),
      flex: 1,
      minWidth: 105,
      sortable: true,
      disableColumnMenu: true,
      cellClassName: (params: {
        row: CoreInterfaces.EngagementLettersSummaryItem;
      }) => {
        let cssClassNames = "gt-lettersSummaryCell";
        if (
          params.row.EngagementStatus !==
          Constants.EngagementLetterSummaryStatus.DigitallySignedDeclined
        ) {
          if (!params.row.IsActive) {
            cssClassNames = `${cssClassNames} gt-lettersSummaryCell--deactivated`;
          }
          switch (params.row.ValidityStatus) {
            case Constants.ValidityStatus.Valid: {
              cssClassNames = `${cssClassNames} gt-lettersSummaryCell--valid`;
              break;
            }
            case Constants.ValidityStatus.ToExpire: {
              cssClassNames = `${cssClassNames} gt-lettersSummaryCell--toExpire`;
              break;
            }
            case Constants.ValidityStatus.Expired: {
              cssClassNames = `${cssClassNames} gt-lettersSummaryCell--expired`;
              break;
            }
          }
        }
        return cssClassNames;
      },
      renderCell(params: { row: CoreInterfaces.EngagementLettersSummaryItem }) {
        return (
          <article>
            {params.row.EngagementStatus !==
              Constants.EngagementLetterSummaryStatus.DigitallySignedDeclined &&
              params.row.SigningDate &&
              new Date(params.row.SigningDate).toLocaleDateString([], {
                ...options,
                timeZone: "UTC",
              })}
          </article>
        );
      },
    },
    {
      field: "Office",
      headerName: t("General.Office"),
      flex: 0.9,
      minWidth: 85,
      sortable: true,
      disableColumnMenu: true,
      cellClassName: (params: {
        row: CoreInterfaces.EngagementLettersSummaryItem;
      }) => {
        return `gt-lettersSummaryCell ${
          !params.row.IsActive ? "gt-lettersSummaryCell--deactivated" : ""
        }`;
      },
      renderCell(params: { row: CoreInterfaces.EngagementLettersSummaryItem }) {
        return (
          <article className="gt-whiteSpaceNoWrap">
            <Tooltip title={params.row.Office}>
              <span>{params.row.Office}</span>
            </Tooltip>
          </article>
        );
      },
    },
    {
      field: "BusinessUnit",
      headerName: t("General.BusinessArea"),
      flex: 0.8,
      minWidth: 85,
      sortable: true,
      disableColumnMenu: true,
      cellClassName: (params: {
        row: CoreInterfaces.EngagementLettersSummaryItem;
      }) => {
        return `gt-lettersSummaryCell ${
          !params.row.IsActive ? "gt-lettersSummaryCell--deactivated" : ""
        }`;
      },
      renderCell(params: { row: CoreInterfaces.EngagementLettersSummaryItem }) {
        return (
          <article className="gt-whiteSpaceNoWrap">
            <Tooltip
              title={getSummaryBusinessAreaLabel(params.row.BusinessUnit)}
            >
              <span>
                {getSummaryBusinessAreaLabel(params.row.BusinessUnit)}
              </span>
            </Tooltip>
          </article>
        );
      },
    },
    {
      field: "HasEngagementLetter",
      headerName: t("General.EngagementLetter"),
      flex: 0.8,
      minWidth: 95,
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      cellClassName: (params: {
        row: CoreInterfaces.EngagementLettersSummaryItem;
      }) => {
        return `gt-lettersSummaryCell ${
          !params.row.IsActive ? "gt-lettersSummaryCell--deactivated" : ""
        }`;
      },
      renderCell(params: { row: CoreInterfaces.EngagementLettersSummaryItem }) {
        return params.row.HasEngagementLetter ? (
          <CheckIcon fontSize="small" color="success" />
        ) : (
          <CloseIcon fontSize="small" color="error" />
        );
      },
    },
    {
      field: "EngagementType",
      headerName: t("General.EngagementType"),
      flex: 1.1,
      minWidth: 110,
      sortable: false,
      disableColumnMenu: true,
      cellClassName: (params: {
        row: CoreInterfaces.EngagementLettersSummaryItem;
      }) => {
        return `gt-lettersSummaryCell ${
          !params.row.IsActive ? "gt-lettersSummaryCell--deactivated" : ""
        }`;
      },
      renderCell(params: { row: CoreInterfaces.EngagementLettersSummaryItem }) {
        const engagementTypeLabel = params.row.EngagementType
          ? t(`Options.EngagementType.${params.row.EngagementType}`)
          : "---";
        return (
          <article className="gt-whiteSpaceNoWrap">
            <Tooltip title={engagementTypeLabel}>
              <span>{engagementTypeLabel}</span>
            </Tooltip>
          </article>
        );
      },
    },
    {
      field: "gtOwner",
      headerName: t("General.GtOwner"),
      flex: 1.2,
      minWidth: 120,
      sortable: true,
      disableColumnMenu: true,
      cellClassName: (params: {
        row: CoreInterfaces.EngagementLettersSummaryItem;
      }) => {
        return `gt-lettersSummaryCell ${
          !params.row.IsActive ? "gt-lettersSummaryCell--deactivated" : ""
        }`;
      },
      valueGetter: (value, row) => {
        return `${row.OwnerFirstName || ""} ${row.OwnerLastName || ""}`;
      },
      renderCell(params: { row: CoreInterfaces.EngagementLettersSummaryItem }) {
        return (
          <article className="gt-whiteSpaceNoWrap">
            <Tooltip
              title={`${params.row.OwnerFirstName} ${params.row.OwnerLastName}`}
            >
              <span>
                {params.row.OwnerFirstName} {params.row.OwnerLastName}
              </span>
            </Tooltip>
          </article>
        );
      },
    },
    {
      field: "Version",
      headerName: t("General.Version"),
      flex: 0.6,
      minWidth: 70,
      sortable: true,
      disableColumnMenu: true,
      renderCell(params: { row: CoreInterfaces.EngagementLettersSummaryItem }) {
        return <article>{params.row.Version}</article>;
      },
    },
    {
      field: "EngagementStatus",
      headerName: t("General.Status"),
      flex: 0.9,
      minWidth: 85,
      sortable: true,
      disableColumnMenu: true,
      valueGetter: (params: GridValueGetterParams) =>
        t(`Options.EngagementLetterSummaryStatus.${params}`),
      renderCell(params: { row: CoreInterfaces.EngagementLettersSummaryItem }) {
        return (
          <article>
            <Tooltip
              title={t(
                `Options.EngagementLetterSummaryStatus.${params.row.EngagementStatus}`
              )}
            >
              <span>
                {t(
                  `Options.EngagementLetterSummaryStatus.${params.row.EngagementStatus}`
                )}
              </span>
            </Tooltip>
          </article>
        );
      },
    },
    {
      field: "Team",
      headerName: t("General.Team"),
      flex: 0.8,
      minWidth: 75,
      sortable: true,
      valueGetter: (params: Team) => params?.DisplayName,
      disableColumnMenu: true,
      renderCell(params: { row: CoreInterfaces.EngagementLettersSummaryItem }) {
        return (
          <article>
            <Tooltip title={params.row.Team?.DisplayName}>
              <span>{params.row.Team?.DisplayName}</span>
            </Tooltip>
          </article>
        );
      },
    },
    {
      field: "LastModifiedDate",
      headerName: t("General.ModifiedDate"),
      flex: 1,
      minWidth: 105,
      sortable: true,
      disableColumnMenu: true,
      renderCell(params: { row: CoreInterfaces.EngagementLettersSummaryItem }) {
        return (
          <article>
            {new Date(params.row.LastModifiedDate).toLocaleDateString([], {
              ...options,
              timeZone: "UTC",
            })}
          </article>
        );
      },
    },
    {
      field: "actions",
      flex: 0.5,
      minWidth: 80,
      headerName: "",
      sortable: false,
      disableColumnMenu: true,
      renderCell(params: { row: CoreInterfaces.EngagementLettersSummaryItem }) {
        return (
          <article className="gt-lettersSummaryActionCell">
            {params.row.IsActive && params.row.InactivationDate && (
              <Tooltip
                title={t("General.PlannedInactivationDateTooltip", {
                  date: formatInactivationDate(params.row.InactivationDate),
                })}
              >
                <EventBusyOutlinedIcon
                  className="gt-lettersSummaryActionCell__inactivationIcon"
                  fontSize="small"
                />
              </Tooltip>
            )}
            <NavLink
              target="_blank"
              to={`/?${Constants.QueryParams.businessOpportunityId}=${params.row.BusinessOpportunityId}&${Constants.QueryParams.clientId}=${params.row.ClientId}&${Constants.QueryParams.unit}=${params.row.BusinessUnit}&${Constants.QueryParams.engagementConfigurationId}=${params.row.Id}`}
            >
              <Tooltip title={t("General.OpenEngagementLinkTootip")}>
                <OpenInNewOutlinedIcon
                  className="gt-lettersSummaryActionCell__linkIcon"
                  fontSize="small"
                />
              </Tooltip>
            </NavLink>
            <GtEngagementLetterMenu
              isAdminOrOfficeManager={
                isAdministrator(appState) || isOfficeManager(appState)
              }
              engagementLetter={params.row}
              onReactivate={() => {
                onReactivateFn(params.row);
              }}
              onInactivateDialogOpen={() => {
                onInactivateDialogOpenFn(params.row);
              }}
              onRemoveInactivationDate={() => {
                onRemoveInactivationDateFn(params.row);
              }}
              onChangeOwnerDialogOpen={function (): void {
                onChangeOwnerDialogOpenFn(params.row);
              }}
              onChangeTeamDialogOpen={function (): void {
                onChangeTeamDialogOpenFn(params.row);
              }}
            />
          </article>
        );
      },
    },
  ];
}

export default EngagementLettersSummaryViewPanel;
